import isEqual from 'lodash/isEqual';
import React, { useEffect, useState } from 'react';

import { usePrevious } from '@wetransfer/transfer/hooks/usePrevious';
import { useAdBlockDetection } from '@wetransfer/transfer/modules/ad-block-detection/hooks';
import { WALLPAPER_ERROR_MESSAGES } from '@wetransfer/transfer/modules/ad-sdk-wallpaper/constants';
import { emitWallpaperInitialized } from '@wetransfer/transfer/modules/ad-sdk-wallpaper/helpers';
import { useWallpaper } from '@wetransfer/transfer/modules/ad-sdk-wallpaper/hooks';
import ErrorHandler from '@wetransfer/transfer/modules/error-handler';
import { useExperiments } from '@wetransfer/transfer/modules/experiments/hooks';
import termsAndConditions from '@wetransfer/transfer/modules/terms-and-conditions';

import { connector, PropsFromRedux } from './connector';
import { dependencies } from './dependencies';

export const MicroFrontend = ({
  state,
  dispatchActions,
  channelWallpapers = [],
}: PropsFromRedux): JSX.Element => {
  const rootNode = 'micro-frontend';

  const prevState = usePrevious(state);
  const isTermsAccepted = termsAndConditions.areAccepted();
  const prevChannelWallpapers = usePrevious(channelWallpapers);

  const { isReady: isExperimentsReady } = useExperiments();

  const { wallpaper } = useWallpaper();

  const { isReady: isAdBlockReady, result: adBlockResult } =
    useAdBlockDetection();

  const [hasWallpaperBundleMounted, setHasWallpaperBundleMounted] =
    useState<boolean>(false);

  useEffect(() => {
    if (hasWallpaperBundleMounted) {
      return;
    }

    if (!isExperimentsReady) {
      return;
    }

    if (!isAdBlockReady) {
      return;
    }

    if (!wallpaper) {
      return;
    }

    const initAndMount = () => {
      wallpaper.init({
        clientId: 'transfer',
        containerId: rootNode,
        isAdBlockEnabled: adBlockResult,
        isTermsAccepted,
        channelWallpapers,
        transferApp: {
          state,
          dispatchActions,
          dependencies,
        },
      });
      emitWallpaperInitialized();

      if (wallpaper.sdkState.isReady) {
        wallpaper.mount();
        setHasWallpaperBundleMounted(true);
      }
    };

    initAndMount();
  }, [
    wallpaper,
    hasWallpaperBundleMounted,
    dispatchActions,
    state,
    channelWallpapers,
    adBlockResult,
    isAdBlockReady,
    isExperimentsReady,
    isTermsAccepted,
  ]);

  useEffect(() => {
    if (!hasWallpaperBundleMounted) {
      return;
    }

    const handleUpdates = () => {
      if (prevState && !isEqual(prevState, state)) {
        try {
          wallpaper.updateState(state);
        } catch (error) {
          ErrorHandler.track(
            new Error(WALLPAPER_ERROR_MESSAGES.UPDATE_STATE_FAILED),
            { errorMessage: error.message }
          );
        }
      }

      if (
        prevChannelWallpapers &&
        !isEqual(prevChannelWallpapers, channelWallpapers)
      ) {
        try {
          wallpaper.actions.updateChannelWallpapers(channelWallpapers);
          wallpaper.actions.rotate();
        } catch (error) {
          ErrorHandler.track(
            new Error(WALLPAPER_ERROR_MESSAGES.UPDATE_CHANNEL_FAILED),
            { errorMessage: error.message }
          );
        }
      }
    };

    handleUpdates();
  }, [
    hasWallpaperBundleMounted,
    wallpaper,
    state,
    prevState,
    channelWallpapers,
    prevChannelWallpapers,
  ]);

  useEffect(() => {
    if (!hasWallpaperBundleMounted) {
      return;
    }

    if (isTermsAccepted) {
      try {
        wallpaper.updateTerms(isTermsAccepted);
      } catch (e) {
        ErrorHandler.track(
          new Error(WALLPAPER_ERROR_MESSAGES.UPDATE_TERMS_FAILED),
          { errorMessage: e.message }
        );
      }
    }
  }, [isTermsAccepted, wallpaper, hasWallpaperBundleMounted]);

  return <div id={rootNode} data-testid={rootNode} />;
};

export default connector(MicroFrontend);
